import {
  IStaffer,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { asReffable, Firestore, Region } from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Brand } from '../../brand';
import { Staffer } from '../../staffer/staffer';
import { Practice } from '../practice';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
} from './practice-setup-step.types';
import {
  initRawSchema,
  toListContent,
  toParagraphContent,
} from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/brand/{brandId}/staff'],
  validationMode: 'count',
};

export const providerNumbersStepAU: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.ProviderNumbers,
  category: PracticeSetupStepCategory.GeneralPracticeSettings,
  title: 'Set Up Practitioner Settings',
  description: initRawSchema([
    toParagraphContent(
      `Ensure all practitioners have relevant provider numbers and online profile settings set up. The following provider numbers will need to be filled in for claims to be processed correctly:`
    ),
    toListContent([
      `Practice`,
      `Modality`,
      `Provider Number`,
      `Prescriber Number`,
      `Online Profile Settings (if applicable)`,
    ]),
  ]),
  dependsOn: [PracticeSetupStepId.StaffSetup],
  regions: [Region.Australia],
  getRoute: (brand, _practice) => `settings/brand/${brand.ref.id}/staff`,
  validate$: (practice$, _step, _options) => {
    const region$ = practice$.pipe(
      switchMap((practice) =>
        Firestore.getDoc(
          Brand.organisationRef(asReffable(Practice.brandRef(practice)))
        )
      ),
      map((organisation) => organisation.region)
    );

    return combineLatest([practice$, region$]).pipe(
      switchMap(([practice]) =>
        Staffer.practitionersByPractice$(practice).pipe(
          map((practitioners) => {
            const missingPractitionerNames: string[] = [];
            const totalPractitioners = practitioners.length;

            for (const practitioner of practitioners) {
              if (!hasRequiredProviderFields(practitioner)) {
                missingPractitionerNames.push(
                  practitioner.user?.name || 'Unknown'
                );
              }
            }

            if (missingPractitionerNames.length > 0) {
              const completedCount =
                practitioners.length - missingPractitionerNames.length;

              return {
                valid: false,
                reason: `Completed ${completedCount}/${
                  practitioners.length
                } practitioners. Missing provider details for: \r\n${missingPractitionerNames
                  .sort()
                  .map((name) => `- ${name}`)
                  .join('\r\n')}`,
                progress: {
                  current: completedCount,
                  total: totalPractitioners,
                },
              };
            }

            return { valid: true };
          })
        )
      )
    );
  },
  pageVisitTracking,
};

function hasRequiredProviderFields(practitioner: IStaffer): boolean {
  return !!practitioner.providerDetails?.some(
    (provider) => !!provider.providerNumber?.trim()
  );
}
